import { Dispatch } from 'redux';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { updateUser } from './users';
import { notifyError } from '../utils/functions';
import * as api from '../utils/api';

interface ReportState {
  data?: IReportData;
  loadDate: string; // This is the date and time the report was last loaded
  reportSettingsPaneOpen: boolean;
  batchIsLoading: boolean;
  batchProgress: number;
  batchError: string | null;
  // True between knowing an auto-load will fire (teams preselected) and its batch starting — the
  // pre-batch window where courses are still being fetched. Combined with batchIsLoading in
  // selectIsFilterLocked to lock the report filters for the whole load. See PA-3284.
  initialAutoLoadPending: boolean;
  // True when the last batch ended without data in a way the selection cannot recover from on its
  // own, since it hasn't changed: a cancel via batchCancel, or a failure. Drives the not-loaded
  // canvas. (CLEReport's Cancel goes through batchAbort instead and so does not set this — the
  // not-loaded canvas is out of scope there.)
  //
  // Deliberately NOT set by batchAbort, which is reached from unmount, from the pre-trigger
  // teardown in the report components, and from CLEReport's Cancel. (No count given on purpose —
  // it has been miscounted in three successive revisions of this comment, because "dispatch site",
  // "call path" and "caller" give different answers. Grep for batchAbort.)
  //
  // The unmount route is the one that matters here: setting the flag there would leave the
  // not-loaded canvas showing on the next visit. The pre-trigger route is harmless either way —
  // abort and trigger run in one effect body, so batchStart clears the flag in the same React batch
  // and no render sees it set. See PA-3289.
  batchInterrupted: boolean;
  // Whether running the report again could plausibly succeed. A cancel always can; a failure
  // depends on why it failed — a lapsed session returns the same 401 however many times you retry,
  // so the canvas shows the sign-in message and the panel holds Run back, rather than inviting an
  // endless loop. Only
  // meaningful while batchInterrupted is true, and both actions that set that flag also write this.
  batchCanRetry: boolean;
  // Incremented every time the user presses Run. The report pages watch it alongside the selection,
  // so an explicit run always reaches trigger() even when the selection is byte-for-byte what it
  // already was — the case a cancelled run always lands in, and the one a selection-watching effect
  // cannot see. See PA-3289.
  runRequestId: number;
}

interface BatchErrorPayload {
  message: string;
  canRetry: boolean;
}

const initialState: ReportState = {
  data: undefined,
  loadDate: new Date().toISOString(),
  reportSettingsPaneOpen: true,
  batchIsLoading: false,
  batchProgress: 0,
  batchError: null,
  initialAutoLoadPending: false,
  batchInterrupted: false,
  batchCanRetry: true,
  runRequestId: 0,
};

export const reportSlice = createSlice({
  name: 'report',
  initialState,
  reducers: {
    setReportData(state, action: PayloadAction<IReportData>) {
      state.data = action.payload;
    },
    clearReportData(state) {
      state.data = undefined;
      state.batchInterrupted = false;
    },
    updateLoadDate(state, action: PayloadAction<string>) {
      state.loadDate = action.payload;
    },
    toggleReportSettingsPane(state) {
      state.reportSettingsPaneOpen = !state.reportSettingsPaneOpen;
    },
    batchStart(state) {
      state.batchIsLoading = true;
      state.batchProgress = 0;
      state.batchError = null;
      state.data = undefined;
      state.batchInterrupted = false;
      state.batchCanRetry = true;
    },
    batchProgress(state, action: PayloadAction<number>) {
      state.batchProgress = action.payload;
    },
    batchComplete(state, action: PayloadAction<IReportData>) {
      state.batchIsLoading = false;
      state.batchProgress = 100;
      state.batchError = null;
      state.data = action.payload;
      // A delivered report is never an interrupted one. batchStart has already cleared this in
      // every path that exists today; saying it here too means the invariant holds by definition
      // rather than by call order, so a loaded report can't end up behind the cancelled message.
      state.batchInterrupted = false;
      state.initialAutoLoadPending = false;
    },
    batchError(state, action: PayloadAction<BatchErrorPayload>) {
      state.batchIsLoading = false;
      state.batchProgress = 0;
      state.batchError = action.payload.message;
      state.initialAutoLoadPending = false;
      state.batchInterrupted = true;
      state.batchCanRetry = action.payload.canRetry;
    },
    // Dispatched on unmount, before each selection-driven batch trigger, and — still — from
    // CLEReport's overlay Cancel. On GeneralReport/TeamManagerReport a user cancel goes through
    // batchCancel instead. Also clears initialAutoLoadPending so batchIsLoading carries the lock.
    //
    // CLEReport was deliberately left out of PA-3289 entirely, which is two things, not one: it has
    // no not-loaded canvas, AND it still calls useOverlayTransition without the interrupted
    // argument, so cancelling a CLE report continues to announce "Complete!" with a tick and a full
    // progress bar before clearing. That second one is the same false-completion defect PA-3289
    // fixed on the other two pages. Fixing it here is not just the argument: its Cancel goes through
    // batchAbort, which never sets batchInterrupted, so the Cancel would have to move to
    // batchCancel as well. Both are known, not oversights.
    batchAbort(state) {
      state.batchIsLoading = false;
      state.batchProgress = 0;
      state.batchError = null;
      state.data = undefined;
      state.initialAutoLoadPending = false;
      state.batchInterrupted = false;
      // Written even though batchInterrupted = false makes it moot, so that this teardown and
      // batchCancel's differ by exactly one field. Leaving it out made the pair's difference
      // depend on what happened to be in state beforehand.
      state.batchCanRetry = true;
    },
    // Dispatched when the user cancels a running batch from the progress overlay on
    // GeneralReport/TeamManagerReport. Same teardown as batchAbort, plus batchInterrupted so the
    // report area can show the not-loaded canvas.
    batchCancel(state) {
      // Only a run still in flight can be cancelled. The overlay stays mounted for a frame after a
      // terminal with its Cancel button live, and a click there must not rewrite a failure that
      // already happened: it would null the error, report a real failure as one the user chose, and
      // hand back a retry button that was deliberately withheld from a 401.
      if (!state.batchIsLoading) return;

      state.batchIsLoading = false;
      state.batchProgress = 0;
      state.batchError = null;
      state.data = undefined;
      state.initialAutoLoadPending = false;
      state.batchInterrupted = true;
      state.batchCanRetry = true;
    },
    // The user pressed Run. Deliberately a counter rather than a boolean flag: a flag would need
    // clearing once consumed, and until it was cleared a second press would write the value it
    // already holds — an Immer no-op that fires nothing. A value that only ever moves forward is
    // read by comparison, so there is nothing to reset and every press is a change.
    requestReportRun(state) {
      state.runRequestId += 1;
    },
    // Arm/disarm the pre-batch filter lock. Dispatched from useFetchCoursesOnLoad: armed (true) when
    // an auto-load will fire, disarmed (false) when there is nothing to load. (Batch terminals above
    // clear it separately once a batch actually runs.)
    setInitialAutoLoadPending(state, action: PayloadAction<boolean>) {
      state.initialAutoLoadPending = action.payload;
    },
  },
});

export const {
  setReportData,
  clearReportData,
  updateLoadDate,
  toggleReportSettingsPane,
  batchStart,
  batchProgress,
  batchComplete,
  batchError,
  batchAbort,
  batchCancel,
  requestReportRun,
  setInitialAutoLoadPending,
} = reportSlice.actions;

// The report filters are locked while a batch fetch is in flight (batchIsLoading) or an auto-load
// is pending its batch (initialAutoLoadPending) — together covering the whole load, including the
// pre-batch course-fetch window, so edits can't appear applied without reaching the report.
// Continuity across the handoff is structural: initialAutoLoadPending stays set until a batch
// terminal (complete/error/abort/cancel) clears it, and batchStart raises batchIsLoading before
// that, so at least one flag is always set from arm through load end — no editable gap to design
// around.
//
// (The abort()-before-trigger() ordering in the report components does not serve this lock, and it
// is not what stops a superseded run either: abort nulls the controller ref, so trigger's own
// `abortControllerRef.current?.abort()` finds nothing to abort, and trigger lowers the aborted flag
// before its first await. What makes the old loop exit is axios.isCancel. The ordering earns its
// keep in one case only: with no teams selected, trigger early-returns without tearing anything
// down, so abort is the sole teardown of the in-flight batch.)
export const selectIsFilterLocked = (state: { report: ReportState }): boolean =>
  state.report.batchIsLoading || state.report.initialAutoLoadPending;

// A cancelled or failed batch is the one dead end the selection can't clear on its own — it hasn't
// changed, so no effect will re-trigger.
//
// The batchIsLoading guard is redundant today and is kept as a backstop, not because a live path
// needs it: batchStart is the only writer of batchIsLoading = true and it clears batchInterrupted
// in the same reducer, while both writers of batchInterrupted = true (batchError, batchCancel) clear
// batchIsLoading. So the two flags cannot currently both be set. The guard exists so that a future
// action raising batchIsLoading without clearing batchInterrupted can't put the not-loaded canvas on
// top of a running report.
export const selectCanRegenerateReport = (state: { report: ReportState }): boolean =>
  state.report.batchInterrupted && !state.report.batchIsLoading;

// Whether results are actually on screen, which is the panel's cue that Run has nothing to do.
//
// The batchInterrupted guard is reachable, though only by a race: batchError does not clear data,
// so a superseded run's failure landing after a newer run has completed leaves the new report in
// data with batchInterrupted set. Without the guard, Run would read "up to date" on top of the
// not-loaded canvas. (A cancel never gets here — batchCancel clears data itself.)
export const selectHasLoadedReport = (state: { report: ReportState }): boolean =>
  !!state.report.data && !state.report.batchInterrupted;

// True when the last run failed in a way that running it again cannot fix. The panel holds Run back
// for these, so the "sign in again" message is not sitting next to a live button that reproduces the
// same failure — the loop that message exists to avoid.
export const selectHasUnretryableFailure = (state: { report: ReportState }): boolean =>
  state.report.batchInterrupted && !state.report.batchCanRetry;

// Plain async fetch functions for use with useBatchedReport
export const fetchGeneralReportBatch = async (
  teamId: string,
  deselectedCourseIds: string[],
  signal: AbortSignal,
): Promise<IReportData> => {
  const response = await api.getGeneralReportData([teamId], deselectedCourseIds, signal);
  return response.data as IReportData;
};

export const fetchTeamManagerReportBatch = async (
  teamId: string,
  deselectedCourseIds: string[],
  signal: AbortSignal,
): Promise<IReportData> => {
  const response = await api.getTeamManagerReportData([teamId], deselectedCourseIds, signal);
  return response.data as IReportData;
};

export const fetchCLEReport = async (
  from: Date,
  to: Date,
  signal: AbortSignal,
): Promise<IReportData> => {
  const response = await api.getCLEReportData(from, to, signal);
  return response.data as IReportData;
};

export const updateReportSettings =
  ({ user }: { user: IUser }) =>
  async (dispatch: Dispatch) => {
    try {
      await dispatch(updateUser(user, true) as any);
      return Promise.resolve();
    } catch (e) {
      notifyError(e, dispatch);
      return Promise.reject();
    }
  };
